/**
 * Formatting for numbers, credits, money, dates and durations. One locale (en-US) everywhere, so numbers, dates and
 * copy agree on every screen.
 *
 *   formatNumber(8412)                    "8,412"
 *   formatNumber(0.4567, { maximumFractionDigits: 1 }) "0.5"
 *   formatCompact(14059)                  "14.1K"
 *   formatCredits(1)                      "1 credit"
 *   formatCredits(8412)                   "8,412 credits"
 *   formatCreditsOf(8412, 25000)          "8,412 / 25,000 credits"
 *   formatPercent(0.336)                  "34%"
 *   formatMoney(1900)                     "$19"        (cents in, like the billing API)
 *   formatMoney(1950)                     "$19.50"
 *   formatBytes(812 * 1024 * 1024)        "812 MB"
 *   formatDateTime(iso)                   "Oct 19, 2026, 9:59 AM"
 *   formatDateTime(iso, "date")           "Oct 19, 2026"
 *   formatDateTime(iso, "time")           "9:59 AM"
 *   formatDateTime(iso, "relative")       "5 min ago" (falls back to the date after 7 days)
 *   formatDuration(450)                   "450ms"
 *   formatDuration(3400)                  "3.4s"
 *   formatDuration(125000)                "2m 5s"
 *   formatDuration(3780000)               "1h 3m"
 *   plural(3, "page")                     "3 pages"
 *   formatUrlForDisplay("https://www.google.com/maps/place/data=!4m2!3m1!1s0x88...edf443", 40)
 *                                         "google.com/maps/place/data=!4m2!3…edf443"
 *
 * Every function accepts null/undefined/NaN and returns EMPTY ("–"), so tables never show "NaN" or "undefined".
 */

export const LOCALE = "en-US";
/** Placeholder for a missing value in tables and stats. */
export const EMPTY = "–";

const isNum = (n: unknown): n is number => typeof n === "number" && Number.isFinite(n);
const toNum = (n: unknown): number | null => {
  if (isNum(n)) return n;
  if (typeof n === "string" && n.trim() !== "" && Number.isFinite(Number(n))) return Number(n);
  return null;
};

export function formatNumber(n: number | string | null | undefined, opts: Intl.NumberFormatOptions = {}): string {
  const v = toNum(n);
  return v === null ? EMPTY : v.toLocaleString(LOCALE, { maximumFractionDigits: 2, ...opts });
}

export function formatCompact(n: number | string | null | undefined): string {
  const v = toNum(n);
  return v === null ? EMPTY : v.toLocaleString(LOCALE, { notation: "compact", maximumFractionDigits: 1 });
}

export function plural(n: number, singular: string, pluralForm = `${singular}s`): string {
  return `${formatNumber(n)} ${Math.abs(n) === 1 ? singular : pluralForm}`;
}

export function formatCredits(n: number | string | null | undefined): string {
  const v = toNum(n);
  return v === null ? EMPTY : plural(v, "credit");
}

/** "8,412 / 25,000 credits"; an unknown limit shows as "8,412 credits". */
export function formatCreditsOf(used: number | null | undefined, limit: number | null | undefined): string {
  const u = toNum(used) ?? 0;
  const l = toNum(limit);
  return l === null ? formatCredits(u) : `${formatNumber(u)} / ${formatNumber(l)} credits`;
}

/** Ratio in, percent out: formatPercent(0.336) -> "34%". Pass { fromPercent: true } when the value is already 0-100. */
export function formatPercent(
  ratio: number | null | undefined,
  { digits = 0, fromPercent = false }: { digits?: number; fromPercent?: boolean } = {}
): string {
  const v = toNum(ratio);
  if (v === null) return EMPTY;
  return (fromPercent ? v / 100 : v).toLocaleString(LOCALE, {
    style: "percent",
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  });
}

/** Money from cents (the billing API's unit). Whole amounts drop the decimals. */
export function formatMoney(cents: number | null | undefined, currency = "USD"): string {
  const v = toNum(cents);
  if (v === null) return EMPTY;
  const whole = v % 100 === 0;
  try {
    return (v / 100).toLocaleString(LOCALE, {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    });
  } catch {
    return `$${(v / 100).toFixed(whole ? 0 : 2)}`;
  }
}

export function formatBytes(bytes: number | null | undefined, digits = 1): string {
  const v = toNum(bytes);
  if (v === null) return EMPTY;
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let n = Math.abs(v);
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  const s = i === 0 || n >= 100 ? Math.round(n).toString() : n.toFixed(digits).replace(/\.0+$/, "");
  return `${v < 0 ? "-" : ""}${s} ${units[i]}`;
}

type DateInput = string | number | Date | null | undefined;
const toDate = (d: DateInput): Date | null => {
  if (d === null || d === undefined || d === "") return null;
  const date = d instanceof Date ? d : new Date(d);
  return Number.isNaN(date.getTime()) ? null : date;
};

export type DateStyle = "datetime" | "date" | "time" | "short" | "relative";

/**
 * datetime "Oct 19, 2026, 9:59 AM" | date "Oct 19, 2026" | time "9:59 AM" | short "Oct 19" (or "Oct 19, 2025" in
 * another year) | relative "just now", "5 min ago", "3 h ago", "2 days ago", then the date.
 */
export function formatDateTime(value: DateInput, style: DateStyle = "datetime"): string {
  const d = toDate(value);
  if (!d) return EMPTY;
  switch (style) {
    case "date":
      return d.toLocaleDateString(LOCALE, { month: "short", day: "numeric", year: "numeric" });
    case "time":
      return d.toLocaleTimeString(LOCALE, { hour: "numeric", minute: "2-digit" });
    case "short":
      return d.toLocaleDateString(LOCALE, {
        month: "short",
        day: "numeric",
        ...(d.getFullYear() !== new Date().getFullYear() ? { year: "numeric" } : {}),
      });
    case "relative":
      return formatRelative(d);
    default:
      return d.toLocaleString(LOCALE, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  }
}

export function formatRelative(value: DateInput, now: Date = new Date()): string {
  const d = toDate(value);
  if (!d) return EMPTY;
  const sec = Math.round((now.getTime() - d.getTime()) / 1000);
  if (sec < 0) return formatDateTime(d, "datetime");
  if (sec < 45) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h ago`;
  const days = Math.round(h / 24);
  if (days < 7) return days === 1 ? "yesterday" : `${days} days ago`;
  return formatDateTime(d, "date");
}

/** Milliseconds in. "450ms", "3.4s", "42s", "2m 5s", "1h 3m". */
export function formatDuration(ms: number | null | undefined): string {
  const v = toNum(ms);
  if (v === null || v < 0) return EMPTY;
  if (v < 1000) return `${Math.round(v)}ms`;
  const s = v / 1000;
  if (s < 10) return `${s.toFixed(1).replace(/\.0$/, "")}s`;
  if (s < 60) return `${Math.round(s)}s`;
  const totalMin = Math.floor(s / 60);
  if (totalMin < 60) {
    const rem = Math.round(s - totalMin * 60);
    return rem ? `${totalMin}m ${rem}s` : `${totalMin}m`;
  }
  const h = Math.floor(totalMin / 60);
  const m = totalMin - h * 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** Duration between two timestamps, e.g. a job's started_at and finished_at. */
export function formatElapsed(start: DateInput, end: DateInput): string {
  const a = toDate(start);
  const b = toDate(end);
  return a && b ? formatDuration(b.getTime() - a.getTime()) : EMPTY;
}

/**
 * A long URL made readable for one line: no protocol, no "www.", the host kept in full, and a long path (with its
 * query) cut in the middle so both its start and its last characters stay visible. `budget` is the most characters
 * to return (the host alone may exceed it). Keep the full URL in a `title` and offer a copy action next to it.
 *   formatUrlForDisplay("https://www.google.com/maps/place/data=!4m2!3m1!1s0x880faf91a22931d5:0x5d9c722d77edf443", 40)
 *     -> "google.com/maps/place/data=!4m2!3…edf443"
 * `{ query: false }` drops the query and fragment (domain and path only).
 */
export function formatUrlForDisplay(url: string | null | undefined, budget = 64, opts: { query?: boolean } = {}): string {
  if (!url) return "";
  let s = url.trim().replace(/^[a-z][a-z0-9+.-]*:\/\//i, "").replace(/^www\./i, "");
  if (opts.query === false) s = s.split(/[?#]/)[0];
  const slash = s.search(/[/?#]/);
  const host = slash === -1 ? s : s.slice(0, slash);
  let rest = slash === -1 ? "" : s.slice(slash);
  if (rest === "/") rest = "";
  else if (/^\/[^?#]*\/$/.test(rest)) rest = rest.replace(/\/+$/, "");
  try {
    rest = decodeURI(rest);
  } catch {
    // Malformed escapes: show the path as given.
  }
  const room = Math.max(14, budget - host.length);
  if (rest.length <= room) return host + rest;
  const tail = 6;
  const head = room - tail - 1;
  return `${host}${rest.slice(0, head)}…${rest.slice(-tail)}`;
}

/**
 * Shortens text for a list cell: at most `max` characters, cut at a word boundary when one is close, with "…".
 * Counts whole characters (emoji and CJK stay intact). Use the full text in a title attribute.
 *   clampText("Next.js proxy.ts Explained (with Cheat Sheet) - DEV Community", 40) -> "Next.js proxy.ts Explained (with Cheat…"
 */
export function clampText(text: string | null | undefined, max: number): string {
  const s = (text ?? "").replace(/\s+/g, " ").trim();
  const chars = Array.from(s);
  if (chars.length <= max) return s;
  let cut = chars.slice(0, max).join("");
  const space = cut.lastIndexOf(" ");
  if (space > max * 0.7) cut = cut.slice(0, space);
  return cut.replace(/[\s,;:.\-|–]+$/, "") + "…";
}
